import { Box, type SxProps, type Theme } from '@mui/material';

import { TroveOnlyBadge, TROVE_ONLY_COLOR } from './TroveOnlyBadge';
import { useIsTroveUser } from './useIsTroveUser';

interface TroveOnlyProps {
  children: React.ReactNode;
  /** Extra styles for the orange frame (e.g. spacing in its parent's layout). */
  sx?: SxProps<Theme>;
}

/**
 * Renders a page component only for Trove employees, inside a Trove-orange frame with a
 * "Trove only" badge in its upper-right corner so staff can tell partners won't see it. External
 * users (and everyone, until the identity resolves) get nothing.
 *
 * For a whole tab, set `troveOnly` on its SectionTab instead.
 */
export const TroveOnly = ({ children, sx }: TroveOnlyProps) => {
  const { isTroveUser } = useIsTroveUser();
  if (!isTroveUser) return null;

  return (
    <Box
      data-testid="trove-only"
      sx={[
        {
          position: 'relative',
          border: '1px solid',
          borderColor: TROVE_ONLY_COLOR,
          borderRadius: 1,
          p: 1.5,
          // Room for the badge, which straddles the top border.
          pt: 2,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <TroveOnlyBadge sx={{ position: 'absolute', top: -9, right: 12 }} />
      {children}
    </Box>
  );
};

export default TroveOnly;
